// Shares the Watch-section self-check ("Can you answer these?") with the
// registration form so a registrant's "Not sure" answers can pre-fill their
// Biggest CPF Question. Persists for the tab (sessionStorage) and notifies
// live listeners via a window event, so the form updates even when the
// modal is already mounted.
//
// Storage review: only the *ids* of checklist questions the visitor marked
// "Not sure" are stored (e.g. ["payout_age", "frs"]). No personal data, no
// free text. sessionStorage is chosen so the hint is tab-scoped and gone when
// the tab closes.
import { eventConfig } from "@/config/eventConfig";
import { logger } from "@/lib/logger";

const KEY = "cpf_selfcheck_v1";
export const SELF_CHECK_EVENT = "selfcheck:change";
export const UNSURE_PREFIX = "Not sure about:";

const safeStorage = () => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

/** Persist answers ({ [index]: "yes" | "no" }) and broadcast the change. */
export function saveSelfCheck(answers) {
  const s = safeStorage();
  try {
    if (s) s.setItem(KEY, JSON.stringify(answers || {}));
  } catch (e) {
    // Storage full / blocked: the live event below still updates open forms.
    logger.debug("[selfCheck] could not persist answers", e);
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SELF_CHECK_EVENT, { detail: answers || {} }));
  }
}

export function loadSelfCheck() {
  const s = safeStorage();
  try {
    return s ? JSON.parse(s.getItem(KEY) || "{}") : {};
  } catch {
    return {};
  }
}

/** The question texts the visitor marked "Not sure", in on-screen order. */
export function unsureQuestions(answers = loadSelfCheck()) {
  const qs = (eventConfig.video && eventConfig.video.questions) || [];
  return qs.map((item, i) => (answers[i] === "no" ? item.q : null)).filter(Boolean);
}

/** Text placed into "Biggest CPF Question" when the visitor was unsure of something. */
export function buildPrefill(answers = loadSelfCheck()) {
  const list = unsureQuestions(answers);
  if (!list.length) return "";
  return `${UNSURE_PREFIX}\n${list.map((q) => `\u2022 ${q}`).join("\n")}`;
}

/** Subscribe to live changes; returns an unsubscribe function. */
export function onSelfCheckChange(handler) {
  if (typeof window === "undefined") return () => {};
  const fn = (e) => handler(e.detail || loadSelfCheck());
  window.addEventListener(SELF_CHECK_EVENT, fn);
  return () => window.removeEventListener(SELF_CHECK_EVENT, fn);
}
